import { compassDirection, formatValue } from './surf-forecast.mjs'

export const SURF_CONDITION_STATES = Object.freeze({
  potential: { label: 'Potentially good', symbol: '✓' },
  less: { label: 'Less favourable', symbol: '~' },
  danger: { label: 'Dangerous', symbol: '!' },
  unconfirmed: { label: 'Unconfirmed', symbol: '?' },
})

// Keep local observations separate from provisional interpretation limits so
// future calibration does not require changing the timeline component.
export const SURF_CONDITION_GUIDANCE = Object.freeze({
  'mawi-beach': {
    ability: 'Reef break · Not suitable for beginners',
    preference: 'Easterly wind · mid tide preferred; higher tide can work, especially for the right. Southwest groundswell preferred. Light SE wind needs a local check.',
    windFrom: [67.5, 112.5], tide: 'mid', dangerousOffshoreMetres: 5,
    higherTideCanWork: true, possibleWindDirections: ['ESE', 'SE'],
  },
  'serangan-beach': {
    ability: 'Reef break · Not suitable for beginners',
    preference: 'Local preference: low tide with NW–NNW or nearly still wind.',
    windFrom: [303.75, 348.75], tide: 'low',
  },
  'selong-belanak-beach': {
    ability: 'Beginner lessons on suitable days',
    preference: 'Billa Surf School usually suggests 09:00–15:00 WITA. Preferred tide awaiting confirmation; the beach is relatively sheltered from wind.',
    tide: null,
  },
})

// Conservative starting limits, NOT locally validated surf/safety thresholds.
// Green assesses wind and estimated tide only; swell is displayed for a local
// check. Mawi's 5 m offshore danger threshold is the owner's confirmed rule.
export const SURF_CONDITION_LIMITS = Object.freeze({
  nearlyStillKnots: 3, favourableWindKnots: 10, favourableGustKnots: 15,
  lowTideFraction: 1 / 3, midTideLower: 1 / 3, midTideUpper: 2 / 3,
})

const finite = value => typeof value === 'number' && Number.isFinite(value)
const nonnegative = value => finite(value) && value >= 0
const HOUR = 3600

// Locate the hour between adjacent high/low events, including across midnight.
// Never use zero MSL or a partial day's min/max as a proxy for low/mid tide,
// and never infer a cycle across gaps or an incomplete forecast boundary.
export function tideCycleFraction(tide, time) {
  if (!finite(time) || tide?.stale) return null
  const events = tide?.events || []
  const levels = new Map((tide?.hours || []).map(row => [row.time, row.level]))
  const level = levels.get(time)
  if (!finite(level)) return null
  for (let i = 0; i < events.length - 1; i++) {
    const before = events[i], after = events[i + 1]
    if (time < before.time || time > after.endTime) continue
    if (!['high', 'low'].includes(before.type) || !['high', 'low'].includes(after.type) || before.type === after.type || after.time <= before.endTime || after.endTime - before.time > 12 * HOUR) return null
    for (let sample = before.time; sample <= after.endTime; sample += HOUR) if (!finite(levels.get(sample))) return null
    const low = Math.min(before.level, after.level), high = Math.max(before.level, after.level)
    if (!finite(low) || !finite(high) || high - low < .000001 || level < low || level > high) return null
    return (level - low) / (high - low)
  }
  return null
}

const result = (state, reason) => ({ state, ...SURF_CONDITION_STATES[state], reason, confidence: 'low' })

export function surfCondition(spotSlug, hour, tide, { stale = false } = {}) {
  const guidance = SURF_CONDITION_GUIDANCE[spotSlug]
  if (!guidance || !hour) return result('unconfirmed', 'Conditions guidance is unavailable for this hour.')
  // Hazard takes precedence even when other inputs are missing or stale.
  if (guidance.dangerousOffshoreMetres && nonnegative(hour.wave_height) && hour.wave_height >= guidance.dangerousOffshoreMetres) {
    return result('danger', `Offshore waves ${formatValue(hour.wave_height)} m · large-wave danger at Mawi.${stale ? ' Older forecast shown.' : ''}`)
  }
  if (stale) return result('unconfirmed', 'Older forecast · check for updated conditions.')
  if (!guidance.tide) return result('unconfirmed', 'Preferred tide awaiting confirmation. Check lesson conditions with a local instructor.')
  if (!nonnegative(hour.wave_height) || !nonnegative(hour.swell_wave_height) || !nonnegative(hour.swell_wave_period) || !finite(hour.swell_wave_direction)) return result('unconfirmed', 'Wave or swell information is incomplete.')
  if (!nonnegative(hour.wind_speed_10m) || !nonnegative(hour.wind_gusts_10m)) return result('unconfirmed', 'Wind or gust information is incomplete.')
  const { nearlyStillKnots, favourableWindKnots, favourableGustKnots, lowTideFraction, midTideLower, midTideUpper } = SURF_CONDITION_LIMITS
  const calm = hour.wind_speed_10m <= nearlyStillKnots && hour.wind_gusts_10m <= nearlyStillKnots
  const direction = hour.wind_direction_10m
  if (!calm && (!finite(direction) || direction < 0 || direction > 360)) return result('unconfirmed', 'Wind direction is unavailable.')
  if (hour.wind_speed_10m > favourableWindKnots || hour.wind_gusts_10m > favourableGustKnots) return result('less', 'Stronger wind or gusts · less favourable under the initial wind guidance.')
  const offshore = direction >= guidance.windFrom[0] && direction <= guidance.windFrom[1]
  // Published Mawi guidance describes a terrain effect in light SE flow, but
  // there is no locally calibrated speed boundary. Keep it grey, not green.
  const possibleWind = !calm && !offshore && guidance.possibleWindDirections?.includes(compassDirection(direction))
  if (!calm && !offshore && !possibleWind) return result('less', `Wind from ${compassDirection(direction)} · outside the locally preferred direction.`)
  if (hour.wave_height === 0 || hour.swell_wave_height === 0 || hour.swell_wave_period === 0) return result('less', 'Little offshore wave or swell signal · check whether waves are breaking locally.')
  const fraction = tideCycleFraction(tide, hour.time)
  if (fraction === null) return result('unconfirmed', 'Estimated tide cycle is incomplete or older · confirm the tide locally.')
  const higherTide = guidance.higherTideCanWork && fraction > midTideUpper
  const tideMatches = guidance.tide === 'low' ? fraction <= lowTideFraction : fraction >= midTideLower && (fraction <= midTideUpper || higherTide)
  if (!tideMatches) return result('less', guidance.higherTideCanWork ? 'Estimated low tide · shallow reef at Mawi; mid tide preferred.' : `Outside the locally preferred ${guidance.tide} tide · confirm conditions at the reef.`)
  if (possibleWind) return result('unconfirmed', `Wind from ${compassDirection(direction)} may stay clean in Mawi’s valley, but local exposure is uncertain. Check the wind at the reef.`)
  if (higherTide) return result('potential', `${calm ? 'Nearly still wind' : `Light wind from ${compassDirection(direction)}`} · estimated higher tide. Mawi’s right can still work; check swell and breaking waves locally.`)
  return result('potential', `${calm ? 'Nearly still wind' : `Light wind from ${compassDirection(direction)}`} · estimated ${guidance.tide} tide. Check swell and breaking waves locally.`)
}
